import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Link } from '@mui/material';
import moment from 'moment';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import { CookLeavesService } from '../../services/CookLeavesService';
import { UserType } from '../../enums/common';
import { maskPhoneNumber } from '../../utils/PhoneNumberUtils';

interface InputProps {
    cookId: number;
}

const cookLeaveService = new CookLeavesService();

function getLeavesDeletedByLink(id: number, type: UserType): string {
    if (type === UserType.ADMIN) return `/#/AdminUsers/${id}/show`;
    if (type === UserType.COOK) return `/#/Cooks/${id}/show`;
    return `/#/Customers/${id}/show`;
}

const COLUMNS: CommonListColumn[] = [
    { label: 'Id', field: 'id' },
    {
        label: 'Cook Id',
        render: (row) => <Link href={`/#/Cooks/${row.cookId}/show`}>{row?.cookId}</Link>,
    },
    { label: 'Cook Zoho', render: (row) => row?.cookZohoId ?? '-' },
    { label: 'Cook Fname', render: (row) => row?.cookFirstName ?? '-' },
    { label: 'Cook Lname', render: (row) => row?.cookLastName ?? '-' },
    { label: 'Cook Mobile', render: (row) => maskPhoneNumber(row?.cookPhoneNumber) ?? '-' },
    { label: 'Leave Type', field: 'leaveType' },
    {
        label: 'Leave Start Date',
        nowrap: true,
        render: (row) =>
            row?.leaveStartDate
                ? moment(row.leaveStartDate, 'YYYY-MM-DD').format('DD MMM YYYY') +
                  (row?.startTime ? ` ${row.startTime}` : '')
                : '-',
    },
    {
        label: 'Leave End Date',
        nowrap: true,
        render: (row) =>
            row?.leaveEndDate
                ? moment(row.leaveEndDate, 'YYYY-MM-DD').format('DD MMM YYYY') +
                  (row?.endTime ? ` ${row.endTime}` : '')
                : '-',
    },
    {
        label: 'Created At',
        nowrap: true,
        render: (row) => (row?.createdAt ? moment(row.createdAt).format('DD MMM YYYY  hh:mm A') : '-'),
    },
    {
        label: 'Updated At',
        nowrap: true,
        render: (row) => (row?.updatedAt ? moment(row.updatedAt).format('DD MMM YYYY  hh:mm A') : '-'),
    },
    {
        label: 'Deleted At',
        nowrap: true,
        render: (row) => (row?.deletedAt ? moment(row.deletedAt).format('DD MMM YYYY hh:mm A') : '-'),
    },
    {
        label: 'Show',
        stopRowClick: true,
        render: (row) => (
            <Link href={`/#/CookLeaves/${row?.id}/show`}>
                <Button variant="contained" color="primary" size="small">Show</Button>
            </Link>
        ),
    },
    {
        label: '',
        stopRowClick: true,
        render: (row) =>
            row?.deletedById ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                    <span style={{ fontSize: '11px', color: '#7F1D1D', fontWeight: 500 }}>Deleted By</span>
                    <Link href={getLeavesDeletedByLink(row?.deletedById, row?.deletedByType)}>
                        {row?.deletedById}
                    </Link>
                    <span style={{ fontSize: '11px', color: '#666' }}>{row?.deletedByType}</span>
                </div>
            ) : (
                <Link href={`/#/CookLeaves/${row.id}`}>
                    <Button variant="contained" color="primary" size="small">Edit</Button>
                </Link>
            ),
    },
];

const CookLeavesTab = ({ cookId }: InputProps) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const refreshData = useCallback(() => {
        setLoading(true);
        cookLeaveService
            .getAllLeavesForCookById(cookId, rowsPerPage, page * rowsPerPage, { order: 'DESC', field: 'id' })
            .then((res: any) => {
                setData(res?.data ?? []);
                setTotal(res?.total ?? 0);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [cookId, rowsPerPage, page]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    return (
        <Box sx={{ height: 'calc(100vh - 220px)', display: 'flex', flexDirection: 'column' }}>
            <CommonListTable
                columns={COLUMNS}
                data={data}
                loading={loading}
                page={page}
                rowsPerPage={rowsPerPage}
                total={total}
                onPageChange={setPage}
                onRowsPerPageChange={(rows) => { setRowsPerPage(rows); setPage(0); }}
                emptyText="No Cook Leaves Found"
                fillHeight
            />
        </Box>
    );
};

export default CookLeavesTab;
